// src/pages/seeker/SeekerCVImport.jsx
// Review page — tabbed interface

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircle, Loader, AlertCircle, User,
  X, Phone, Mail, Link2, MapPin,
} from 'lucide-react';
import { PhoneCapture, parsePhoneStr, joinPhoneStr } from '../../components/shared/PhoneInput';

const C = {
  navy: '#0e4e95', blue: '#3D80CC', white: '#ffffff', offwhite: '#f4f7fb',
  text: '#1a1a1a', muted: '#666', border: '#d0daea',
  green: '#28A745', greenBg: '#f0fdf4', greenBorder: '#86efac',
  amber: '#b45309', amberBg: '#fff8e6', amberBorder: '#fcd34d',
};

const TAB_NAMES = [
  'ID & Contacts', 'Professional Summary', 'Education',
  'Work Experience', 'Skills', 'Languages', 'References',
];

// ── Shared UI ─────────────────────────────────────────

const TabBar = ({ active, onChange }) => (
  <div style={{ display:'flex', borderBottom:`2px solid ${C.border}`, marginBottom:'20px', overflowX:'auto', overflowY:'hidden' }}>
    {TAB_NAMES.map((name, i) => (
      <button key={i} onClick={() => onChange(i)} style={{
        padding:'10px 16px', fontSize:'13px', fontWeight: active === i ? 700 : 500,
        color: active === i ? C.blue : C.muted, background:'none', border:'none',
        borderBottom:`2px solid ${active === i ? C.blue : 'transparent'}`, marginBottom:'-2px',
        cursor:'pointer', whiteSpace:'nowrap', fontFamily:"'Exo 2', sans-serif",
        transition:'color 0.15s',
      }}>
        {name}
      </button>
    ))}
  </div>
);

const SubFrame = ({ title, icon, children }) => (
  <div style={{ border:`1px solid ${C.border}`, borderRadius:'4px', marginBottom:'12px', overflow:'hidden' }}>
    <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 12px', background:C.blue }}>
      {icon && <span style={{ color:'rgba(255,255,255,0.8)', display:'flex' }}>{icon}</span>}
      <span style={{ fontSize:'12px', fontWeight:700, color:'#fff', fontFamily:"'Exo 2', sans-serif" }}>{title}</span>
    </div>
    <div style={{ padding:'14px' }}>{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom:'12px' }}>
    <label style={{ display:'block', fontSize:'11px', fontWeight:700, color:C.blue, textTransform:'uppercase', marginBottom:'4px' }}>
      {label}{required && <span style={{ color:'#CC0000', marginLeft:'2px' }}>*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, disabled, list }) => (
  <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} list={list} autoComplete={list ? 'off' : undefined}
    style={{ width:'100%', padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background: disabled ? C.offwhite : C.white, boxSizing:'border-box' }} />
);

const Textarea = ({ value, onChange, rows = 3, disabled, maxLength }) => (
  <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} disabled={disabled} maxLength={maxLength}
    style={{ width:'100%', padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, resize:'vertical', background: disabled ? C.offwhite : C.white, boxSizing:'border-box' }} />
);

const CharCount = ({ current, max }) => {
  const pct   = current / max;
  const color = pct > 0.9 ? '#CC0000' : pct > 0.75 ? '#b45309' : C.muted;
  return (
    <div style={{ fontSize:'11px', color, textAlign:'right', marginTop:'4px' }}>
      {current} / {max}
    </div>
  );
};

// ── Phone split/join helpers ──────────────────────────
const parsePhone = (phone) => {
  if (!phone) return { code: '', number: '' };
  const t = phone.trim();
  if (t.startsWith('+')) {
    const sp = t.indexOf(' ');
    return sp > -1 ? { code: t.slice(0, sp), number: t.slice(sp + 1) } : { code: t, number: '' };
  }
  return { code: '', number: t };
};
const joinPhone = (code, number) => [code, number].filter(Boolean).join(' ').trim() || null;

// ── Education qualification levels ─────────────────────
const QUAL_LEVELS = [
  'High School Certificate',
  'Diploma',
  'Advanced Diploma',
  'Graduate Certificate',
  'Graduate Diploma',
  'Bachelor Degree',
  'Master Degree',
  'Doctorate',
];

// ── Education date constants ───────────────────────────
const EDU_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const _now = new Date();
const _cy  = _now.getFullYear();
const _cm  = _now.getMonth() + 1; // 1-12
const EDU_YEARS = Array.from({ length: 60 }, (_, i) => _cy - i);

// ── Experience date helpers ────────────────────────────
const calcDurationMonths = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const norm = (d) => d.length === 4 ? d + '-01' : d;
  const s = new Date(norm(startDate));
  const e = new Date(norm(endDate));
  const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return months > 0 ? String(months) : '';
};

const calcEndDate = (dateStarted, durationMonths) => {
  if (!dateStarted || !durationMonths || parseInt(durationMonths) <= 0) return null;
  const d = new Date(dateStarted + '-01');
  d.setMonth(d.getMonth() + parseInt(durationMonths));
  return d.toISOString();
};

const formatDuration = (months) => {
  const m = parseInt(months, 10);
  if (!m || m <= 0) return null;
  const yrs = Math.floor(m / 12);
  const rem = m % 12;
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} year${yrs !== 1 ? 's' : ''}`);
  if (rem > 0) parts.push(`${rem} month${rem !== 1 ? 's' : ''}`);
  return parts.join(' ');
};

const toISODate = (d) => {
  if (!d) return null;
  if (d.length === 4) return new Date(d + '-01-01T00:00:00').toISOString();
  if (d.length === 7) return new Date(d + '-01T00:00:00').toISOString();
  return new Date(d).toISOString();
};

const AddBtn = ({ onClick, disabled, saving }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ padding:'6px 14px', fontSize:'12px', fontWeight:700, background:C.blue, color:'#fff', border:'none', borderRadius:'4px', cursor: disabled ? 'default' : 'pointer', whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:'4px', opacity: disabled ? 0.6 : 1 }}>
    {saving ? <Loader size={11} style={{ animation:'spin 1s linear infinite' }}/> : null}
    ADD
  </button>
);

const UpdateBtn = ({ onClick, disabled, saving }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ padding:'6px 14px', fontSize:'12px', fontWeight:700, background:C.green, color:'#fff', border:'none', borderRadius:'4px', cursor: disabled ? 'default' : 'pointer', display:'inline-flex', alignItems:'center', gap:'4px', opacity: disabled ? 0.6 : 1 }}>
    {saving ? <Loader size={11} style={{ animation:'spin 1s linear infinite' }}/> : null}
    UPDATE
  </button>
);

const AppliedTick = ({ label }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'12px', fontWeight:600, color:C.green }}>
    <CheckCircle size={14}/> {label || 'Applied'}
  </span>
);

// ── Main page ─────────────────────────────────────────

export const SeekerCVImport = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [cv,        setCv]        = useState(null);
  const [profile,   setProfile]   = useState(null);
  const [polling,   setPolling]   = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [personal,   setPersonal]   = useState({ firstName:'', lastName:'', email:'', phoneCode:'+61', phoneLocal:'', phoneLabel:'', city:'', state:'', country:'', residentialAddress:'' });
  const [cvLinks,      setCvLinks]      = useState([]);
  const [briefSummary, setBriefSummary] = useState('');
  const [fullSummary,  setFullSummary]  = useState('');
  const [experience, setExperience] = useState([]);
  const [education,  setEducation]  = useState([]);
  const [languages,  setLanguages]  = useState([]);
  const [skills,     setSkills]     = useState([]);
  const [references, setReferences] = useState([]);

  // Per-item applied / saving states (Summary tab)
  const [briefApplied, setBriefApplied] = useState(false);
  const [fullApplied,  setFullApplied]  = useState(false);
  const [savingBrief,  setSavingBrief]  = useState(false);
  const [savingFull,   setSavingFull]   = useState(false);

  // Per-item applied / saving states (Personal tab)
  const [nameApplied,      setNameApplied]      = useState(false);
  const [emailApplied,     setEmailApplied]      = useState(false);
  const [phoneApplied,     setPhoneApplied]      = useState(false);
  const [residenceApplied, setResidenceApplied]  = useState(false);
  const [linksApplied,     setLinksApplied]      = useState({});
  const [savingName,       setSavingName]        = useState(false);
  const [savingEmail,      setSavingEmail]       = useState(false);
  const [savingPhone,      setSavingPhone]       = useState(false);
  const [savingResidence,  setSavingResidence]   = useState(false);
  const [savingLinks,      setSavingLinks]       = useState({});

  // Per-item applied / saving states (Experience tab)
  const [expApplied, setExpApplied] = useState({});
  const [savingExp,  setSavingExp]  = useState({});

  // Per-item applied / saving states (Education tab)
  const [eduApplied, setEduApplied] = useState({});
  const [savingEdu,  setSavingEdu]  = useState({});

  // Per-item applied / saving states (Languages tab)
  const [langApplied, setLangApplied] = useState({});
  const [savingLang,  setSavingLang]  = useState({});

  // Per-item applied / saving states (References tab)
  const [refApplied, setRefApplied] = useState({});
  const [savingRef,  setSavingRef]  = useState({});

  // Skills tab applied / saving state
  const [skillsApplied,     setSkillsApplied]     = useState(false);
  const [savingSkills,      setSavingSkills]       = useState(false);
  const [selectedSkillNames, setSelectedSkillNames] = useState(new Set());

  // ── Load ──────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const [cvRes, profileRes, expRes, eduRes, langRes] = await Promise.all([
        seekerAPI.getCVById(id),
        seekerAPI.getProfile(),
        seekerAPI.getExperience(),
        seekerAPI.getEducation(),
        seekerAPI.getLanguages(),
      ]);
      const cvData      = cvRes.data.data;
      const profileData = profileRes.data.data;
      const existingExp  = expRes.data.data  || [];
      const existingEdu  = eduRes.data.data  || [];
      const existingLang = langRes.data.data || [];
      const existingExpKeys = new Set(
        existingExp.map(e => `${(e.company||'').toLowerCase()}|${(e.jobTitle||'').toLowerCase()}`)
      );
      const existingEduKeys = new Set(
        existingEdu.map(e => `${(e.institution||'').toLowerCase()}|${(e.degree||'').toLowerCase()}`)
      );
      const existingLangNames = new Set(
        existingLang.map(l => (l.language||'').toLowerCase())
      );
      setCv(cvData);
      setProfile(profileData);

      if (cvData.parseStatus === 'AWAITING_REVIEW' && cvData.parsedData) {
        const p = cvData.parsedData;
        const locParts = (p.location || '').split(',').map(s => s.trim());
        const parsedPh = parsePhoneStr(p.phone || '');
        setPersonal({
          firstName:          p.name?.firstName || '',
          lastName:           p.name?.lastName  || '',
          email:              p.email           || '',
          phoneCode:          parsedPh.code  || '+61',
          phoneLocal:         parsedPh.number,
          phoneLabel:         '',
          city:               locParts[0]       || '',
          state:              '',
          country:            locParts[1]       || '',
          residentialAddress: '',
        });
        setCvLinks(p.links || []);
        const raw = p.summary || '';
        if (raw.length > 500) { setFullSummary(raw); setBriefSummary(''); }
        else                  { setBriefSummary(raw); setFullSummary(''); }
        setExperience((p.experience || []).map((e, i) => ({
          _key:          i,
          jobTitle:      e.jobTitle     || '',
          schedule:      e.schedule     || '',
          company:       e.company      || '',
          location:      e.location     || '',
          dateStarted:   e.startDate    ? (e.startDate.length === 4 ? e.startDate : e.startDate.slice(0, 7)) : '',
          durationMonths: e.isCurrent   ? '' : calcDurationMonths(e.startDate, e.endDate),
          skills:        (e.skills      || []).map(s => String(s).trim()).filter(Boolean),
        })));
        // Mark already-applied entries (persists across refresh)
        const initialApplied = {};
        (p.experience || []).forEach((e, i) => {
          const key = `${(e.company||'').toLowerCase()}|${(e.jobTitle||'').toLowerCase()}`;
          if (existingExpKeys.has(key)) initialApplied[i] = true;
        });
        setExpApplied(initialApplied);
        const parsedEdu = (p.education || []).map((e, i) => ({ ...e, _key: i, startMonth: e.startMonth || '', endMonth: e.endMonth || '', isCompleted: e.endYear ? e.endYear <= new Date().getFullYear() : true }));
        setEducation(parsedEdu);
        const initialEduApplied = {};
        parsedEdu.forEach((e, i) => {
          const key = `${(e.institution||'').toLowerCase()}|${(e.degree||'').toLowerCase()}`;
          if (existingEduKeys.has(key)) initialEduApplied[i] = true;
        });
        setEduApplied(initialEduApplied);
        const parsedLang = (p.languages || []).map((e, i) => ({ ...e, _key: i }));
        setLanguages(parsedLang);
        const initialLangApplied = {};
        parsedLang.forEach((l, i) => {
          if (existingLangNames.has((l.language||'').toLowerCase())) initialLangApplied[i] = true;
        });
        setLangApplied(initialLangApplied);
        setSkills(p.skills || []);
        setSelectedSkillNames(new Set((p.skills || []).map(s => (s.displayName || '').toLowerCase())));
        setReferences((p.references || []).map((e, i) => {
          const ph = parsePhone(e.phone);
          return { ...e, _key: i, phoneCode: ph.code || '+61', phoneNumber: ph.number, phoneLabel: '' };
        }));
      }
    } catch { toast.error('Failed to load CV data'); }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Poll while parsing
  useEffect(() => {
    if (!cv) return;
    if (cv.parseStatus === 'PENDING' || cv.parseStatus === 'PROCESSING') {
      setPolling(true);
      const timer = setInterval(async () => {
        try {
          const res     = await seekerAPI.getCVById(id);
          const updated = res.data.data;
          if (updated.parseStatus !== 'PENDING' && updated.parseStatus !== 'PROCESSING') {
            clearInterval(timer);
            setPolling(false);
            await loadData();
          }
        } catch { clearInterval(timer); setPolling(false); }
      }, 3000);
      return () => clearInterval(timer);
    } else {
      setPolling(false);
    }
  }, [cv?.parseStatus, id, loadData]);

  // ── Personal tab handlers ─────────────────────────────

  const handleApplyName = async () => {
    setSavingName(true);
    try {
      const updates = {};
      if (personal.firstName) updates.firstName = personal.firstName;
      if (personal.lastName)  updates.lastName  = personal.lastName;
      if (Object.keys(updates).length) await seekerAPI.updateProfile(updates);
      setNameApplied(true);
      toast.success('Name updated', { duration: 1500 });
    } catch { toast.error('Failed to update name'); }
    finally { setSavingName(false); }
  };

  const handleApplyEmail = async () => {
    setSavingEmail(true);
    try {
      await seekerAPI.addContact({ type: 'EMAIL', value: personal.email, isPrimary: true });
      setEmailApplied(true);
      toast.success('Email added', { duration: 1500 });
    } catch { toast.error('Failed to add email'); }
    finally { setSavingEmail(false); }
  };

  const handleApplyPhone = async () => {
    setSavingPhone(true);
    try {
      const phoneValue = joinPhoneStr(personal.phoneCode, personal.phoneLocal);
      await seekerAPI.addContact({ type: 'PHONE', value: phoneValue, label: personal.phoneLabel || null, isPrimary: true });
      setPhoneApplied(true);
      toast.success('Phone added', { duration: 1500 });
    } catch { toast.error('Failed to add phone'); }
    finally { setSavingPhone(false); }
  };

  const handleApplyResidence = async () => {
    setSavingResidence(true);
    try {
      const updates = {};
      if (personal.country)            updates.country            = personal.country;
      if (personal.state)              updates.state              = personal.state;
      if (personal.city)               updates.city               = personal.city;
      if (personal.residentialAddress) updates.residentialAddress = personal.residentialAddress;
      if (Object.keys(updates).length) await seekerAPI.updateProfile(updates);
      setResidenceApplied(true);
      toast.success('Residence updated', { duration: 1500 });
    } catch { toast.error('Failed to update residence'); }
    finally { setSavingResidence(false); }
  };

  const handleApplyLink = async (link, idx) => {
    setSavingLinks(prev => ({ ...prev, [idx]: true }));
    try {
      await seekerAPI.addContact({ type: 'URL', value: link.url, label: link.label || null, isPrimary: false });
      setLinksApplied(prev => ({ ...prev, [idx]: true }));
      toast.success('Link added', { duration: 1500 });
    } catch { toast.error('Failed to add link'); }
    finally { setSavingLinks(prev => ({ ...prev, [idx]: false })); }
  };

  // ── Experience tab handler ────────────────────────────

  const handleApplyExp = async (entry, idx) => {
    if (!entry.dateStarted) { toast.error('Start date is required'); return; }
    setSavingExp(prev => ({ ...prev, [idx]: true }));
    try {
      await seekerAPI.addExperience({
        company:     entry.company     || 'Unknown',
        jobTitle:    entry.jobTitle    || 'Unknown',
        location:    entry.location    || null,
        schedule:    entry.schedule    || null,
        startDate:   toISODate(entry.dateStarted),
        endDate:     calcEndDate(entry.dateStarted, entry.durationMonths),
        isCurrent:   !entry.durationMonths || parseInt(entry.durationMonths) <= 0,
        skills:      entry.skills      || [],
      });
      setExpApplied(prev => ({ ...prev, [idx]: true }));
      toast.success('Work experience added', { duration: 1500 });
    } catch { toast.error('Failed to add experience'); }
    finally { setSavingExp(prev => ({ ...prev, [idx]: false })); }
  };

  // ── Summary tab handler ───────────────────────────────

  const handleApplyBrief = async () => {
    setSavingBrief(true);
    try {
      if (briefSummary) await seekerAPI.updateProfile({ summary: briefSummary });
      setBriefApplied(true);
      toast.success('Brief summary updated', { duration: 1500 });
    } catch { toast.error('Failed to update summary'); }
    finally { setSavingBrief(false); }
  };

  const handleApplyFull = async () => {
    setSavingFull(true);
    try {
      if (fullSummary) await seekerAPI.updateProfile({ fullSummary });
      setFullApplied(true);
      toast.success('Full summary updated', { duration: 1500 });
    } catch { toast.error('Failed to update summary'); }
    finally { setSavingFull(false); }
  };

  // ── Apply functions for other tabs (run on Finish) ────

  const applyEducation = async () => {
    for (const [i, entry] of education.entries()) {
      if (eduApplied[i]) continue;
      if (!entry.degree || !(entry.fieldOfStudy || '').trim()) continue;
      await seekerAPI.addEducation({
        institution:  entry.institution || null,
        degree:       entry.degree,
        fieldOfStudy: entry.fieldOfStudy.trim(),
        startYear:    entry.startYear    || null,
        startMonth:   entry.startYear && entry.startMonth ? parseInt(entry.startMonth) : null,
        endYear:      entry.endYear      || null,
        endMonth:     entry.endYear && entry.endMonth ? parseInt(entry.endMonth) : null,
        isCompleted:  !!entry.endYear,
      }).catch(() => {});
    }
  };

  const applyLanguages = async () => {
    for (const [i, lang] of languages.entries()) {
      if (langApplied[i]) continue;
      if (!lang.language) continue;
      await seekerAPI.addLanguage({ language: lang.language, proficiency: lang.proficiency || 'intermediate' }).catch(() => {});
    }
  };

  const handleApplyNewSkills = async () => {
    const existingNames = new Set(
      (profile?.skills || []).map(s => (s.skill?.displayName || s.displayName || '').toLowerCase())
    );
    const toAdd = skills.filter(s => {
      const key = (s.displayName || '').toLowerCase();
      return !existingNames.has(key) && selectedSkillNames.has(key);
    });
    if (toAdd.length === 0) { setSkillsApplied(true); return; }
    setSavingSkills(true);
    try {
      for (const skill of toAdd) {
        await seekerAPI.addSkill({ displayName: skill.displayName }).catch(() => {});
      }
      setSkillsApplied(true);
      toast.success(`${toAdd.length} skill${toAdd.length !== 1 ? 's' : ''} added`, { duration: 1500 });
    } catch { toast.error('Failed to add skills'); }
    finally { setSavingSkills(false); }
  };

  const applySkills = async () => {
    if (skillsApplied) return;
    const existingNames = new Set(
      (profile?.skills || []).map(s => (s.skill?.displayName || s.displayName || '').toLowerCase())
    );
    for (const skill of skills) {
      if (existingNames.has((skill.displayName || '').toLowerCase())) continue;
      await seekerAPI.addSkill({ displayName: skill.displayName }).catch(() => {});
    }
  };

  const applyReferences = async () => {
    for (const [i, ref] of references.entries()) {
      if (refApplied[i]) continue;
      if (!ref.name) continue;
      await seekerAPI.addReference({
        name:       ref.name,
        jobTitle:   ref.jobTitle || null,
        company:    ref.company  || null,
        email:      ref.email    || null,
        phone:      joinPhone(ref.phoneCode, ref.phoneNumber),
        phoneLabel: ref.phoneLabel || null,
      }).catch(() => {});
    }
  };

  // ── Per-item handlers (Education / Languages / References) ──

  const handleApplyEdu = async (entry, idx) => {
    if (!entry.degree)                          { toast.error('Qualification Level is required'); return; }
    if (!(entry.fieldOfStudy || '').trim())     { toast.error('Field of Study is required'); return; }
    setSavingEdu(prev => ({ ...prev, [idx]: true }));
    try {
      await seekerAPI.addEducation({
        institution:  entry.institution || null,
        degree:       entry.degree,
        fieldOfStudy: entry.fieldOfStudy.trim(),
        startYear:    entry.startYear    || null,
        startMonth:   entry.startYear && entry.startMonth ? parseInt(entry.startMonth) : null,
        endYear:      entry.endYear      || null,
        endMonth:     entry.endYear && entry.endMonth ? parseInt(entry.endMonth) : null,
        isCompleted:  !!entry.endYear,
      });
      setEduApplied(prev => ({ ...prev, [idx]: true }));
      toast.success('Education added', { duration: 1500 });
    } catch { toast.error('Failed to add education'); }
    finally { setSavingEdu(prev => ({ ...prev, [idx]: false })); }
  };

  const handleApplyLang = async (lang, idx) => {
    setSavingLang(prev => ({ ...prev, [idx]: true }));
    try {
      await seekerAPI.addLanguage({ language: lang.language, proficiency: lang.proficiency || 'intermediate' });
      setLangApplied(prev => ({ ...prev, [idx]: true }));
      toast.success('Language added', { duration: 1500 });
    } catch { toast.error('Failed to add language'); }
    finally { setSavingLang(prev => ({ ...prev, [idx]: false })); }
  };

  const handleApplyRef = async (ref, idx) => {
    if (!ref.name) { toast.error('Full name is required'); return; }
    setSavingRef(prev => ({ ...prev, [idx]: true }));
    try {
      await seekerAPI.addReference({
        name:       ref.name,
        jobTitle:   ref.jobTitle || null,
        company:    ref.company  || null,
        email:      ref.email    || null,
        phone:      joinPhone(ref.phoneCode, ref.phoneNumber),
        phoneLabel: ref.phoneLabel || null,
      });
      setRefApplied(prev => ({ ...prev, [idx]: true }));
      toast.success('Reference added', { duration: 1500 });
    } catch { toast.error('Failed to add reference'); }
    finally { setSavingRef(prev => ({ ...prev, [idx]: false })); }
  };

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await Promise.allSettled([
        applyEducation(),
        applyLanguages(),
        applySkills(),
        applyReferences(),
      ]);
      await seekerAPI.markCVReviewed(id);
      toast.success('CV review complete');
      navigate('/seeker/profile');
    } catch {
      toast.error('Failed to complete review');
      setFinishing(false);
    }
  };

  // ── Non-review states ─────────────────────────────────

  if (!cv) return <div style={{ padding:40, textAlign:'center', color:C.muted }}>Loading…</div>;

  if (polling || cv.parseStatus === 'PENDING' || cv.parseStatus === 'PROCESSING') {
    return (
      <div className="fade-in">
        <div className="page-header"><h1>Reviewing Your CV</h1><p>Extracting information from your document</p></div>
        <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'6px', padding:'60px 40px', textAlign:'center', marginTop:'24px' }}>
          <Loader size={40} color={C.blue} style={{ marginBottom:16, animation:'spin 1s linear infinite' }} />
          <h3 style={{ fontSize:'16px', fontWeight:700, color:C.text, margin:'0 0 8px' }}>Parsing your CV…</h3>
          <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>This usually takes a few seconds. The page will update automatically.</p>
        </div>
      </div>
    );
  }

  if (cv.parseStatus === 'FAILED') {
    return (
      <div className="fade-in">
        <div className="page-header"><h1>CV Review</h1></div>
        <div style={{ background:'#fff1f2', border:'1px solid #fecdd3', borderRadius:'6px', padding:'40px', textAlign:'center' }}>
          <AlertCircle size={36} color="#be123c" style={{ marginBottom:12 }} />
          <h3 style={{ fontSize:'16px', fontWeight:700, color:'#be123c', margin:'0 0 8px' }}>Parsing failed</h3>
          <p style={{ fontSize:'13px', color:C.muted, margin:'0 0 16px' }}>We couldn't extract data from this file. Please contact support to re-upload.</p>
          <button onClick={() => navigate('/seeker/profile')} className="btn btn-outline">Back to My CVs</button>
        </div>
      </div>
    );
  }

  if (cv.parseStatus === 'REVIEWED') {
    return (
      <div className="fade-in">
        <div className="page-header"><h1>CV Review</h1></div>
        <div style={{ background:C.greenBg, border:`1px solid ${C.greenBorder}`, borderRadius:'6px', padding:'40px', textAlign:'center' }}>
          <CheckCircle size={36} color={C.green} style={{ marginBottom:12 }} />
          <h3 style={{ fontSize:'16px', fontWeight:700, color:C.green, margin:'0 0 8px' }}>Already reviewed</h3>
          <p style={{ fontSize:'13px', color:C.muted, margin:'0 0 16px' }}>This CV has already been processed.</p>
          <button onClick={() => navigate('/seeker/profile')} className="btn btn-primary">Back to My CVs</button>
        </div>
      </div>
    );
  }

  const nothingExtracted = !personal.firstName && !personal.email && !personal.city && !personal.country && !briefSummary && !fullSummary
    && experience.length === 0 && education.length === 0
    && languages.length === 0 && skills.length === 0 && references.length === 0;

  const d = finishing; // shorthand for disabled

  // ── Derived values ────────────────────────────────────
  const profileSkillNames = new Set(
    (profile?.skills || []).map(s => (s.skill?.displayName || s.displayName || '').toLowerCase())
  );

  const existingEmails = (profile?.contacts || []).filter(c => c.type === 'EMAIL').map(c => c.value.toLowerCase());
  const existingPhones = (profile?.contacts || []).filter(c => c.type === 'PHONE').map(c => c.value);
  const existingUrls   = (profile?.contacts || []).filter(c => c.type === 'URL').map(c => c.value.toLowerCase());
  const emailInProfile = !!(personal.email && existingEmails.includes(personal.email.toLowerCase()));
  const joinedPhone    = joinPhoneStr(personal.phoneCode, personal.phoneLocal);
  const phoneInProfile = !!(joinedPhone && existingPhones.includes(joinedPhone));

  // ── Tab content ───────────────────────────────────────

  const renderTab = () => {
    switch (activeTab) {

      // ── Personal ────────────────────────────────────
      case 0:
        return (
          <>
            {/* Personal Information */}
            <SubFrame title="Personal Information" icon={<User size={13}/>}>
              {personal.firstName || personal.lastName ? (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                    <Field label="First Name">
                      <Input value={personal.firstName} onChange={v => setPersonal(p => ({...p, firstName:v}))} placeholder="First name" disabled={nameApplied || d} />
                    </Field>
                    <Field label="Last Name">
                      <Input value={personal.lastName} onChange={v => setPersonal(p => ({...p, lastName:v}))} placeholder="Last name" disabled={nameApplied || d} />
                    </Field>
                  </div>
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'4px' }}>
                    {nameApplied
                      ? <AppliedTick label="Updated" />
                      : <UpdateBtn onClick={handleApplyName} disabled={savingName || d} saving={savingName} />
                    }
                  </div>
                </>
              ) : (
                <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No name detected in your CV.</p>
              )}
            </SubFrame>

            {/* Contacts */}
            <SubFrame title="Contacts" icon={<Phone size={13}/>}>
              {personal.email || personal.phone ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {personal.email && (
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ flex:1 }}>
                        <Input
                          value={personal.email}
                          onChange={v => setPersonal(p => ({...p, email:v}))}
                          placeholder="Email address"
                          disabled={emailInProfile || emailApplied || d}
                        />
                      </div>
                      {emailInProfile || emailApplied
                        ? <span title="Already exists in the profile" style={{ flexShrink:0, display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                        : <AddBtn onClick={handleApplyEmail} disabled={savingEmail || d} saving={savingEmail} />
                      }
                    </div>
                  )}
                  {personal.phoneLocal && (
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ flex:1 }}>
                        <PhoneCapture
                          code={personal.phoneCode}
                          number={personal.phoneLocal}
                          label={personal.phoneLabel}
                          onCodeChange={v => setPersonal(p => ({...p, phoneCode:v}))}
                          onNumberChange={v => setPersonal(p => ({...p, phoneLocal:v}))}
                          onLabelChange={v => setPersonal(p => ({...p, phoneLabel:v}))}
                          disabled={phoneInProfile || phoneApplied || d}
                        />
                      </div>
                      {phoneInProfile || phoneApplied
                        ? <span title="Already exists in the profile" style={{ flexShrink:0, display:'flex', alignSelf:'flex-start', paddingTop:'4px', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                        : <span style={{ flexShrink:0, alignSelf:'flex-start', paddingTop:'4px' }}><AddBtn onClick={handleApplyPhone} disabled={savingPhone || d} saving={savingPhone} /></span>
                      }
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No contact details detected in your CV.</p>
              )}
            </SubFrame>

            {/* Links */}
            <SubFrame title="Links" icon={<Link2 size={13}/>}>
              {cvLinks.length > 0 ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {cvLinks.map((link, i) => {
                    const inProfile = existingUrls.includes((link.url || '').toLowerCase());
                    const applied   = inProfile || !!linksApplied[i];
                    return (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'8px', flex:1 }}>
                          <Input
                            value={link.url || ''}
                            onChange={v => setCvLinks(prev => prev.map((l,j) => j===i ? {...l, url:v} : l))}
                            placeholder="URL"
                            disabled={applied || d}
                          />
                          <Input
                            value={link.label || ''}
                            onChange={v => setCvLinks(prev => prev.map((l,j) => j===i ? {...l, label:v} : l))}
                            placeholder="Label (e.g. LinkedIn)"
                            disabled={applied || d}
                          />
                        </div>
                        {applied
                          ? <span title="Already exists in the profile" style={{ flexShrink:0, display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                          : <AddBtn onClick={() => handleApplyLink(link, i)} disabled={savingLinks[i] || d} saving={savingLinks[i]} />
                        }
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No links detected in your CV. You can add LinkedIn and portfolio links on your Personal Details page.</p>
              )}
            </SubFrame>

            {/* Residence */}
            <SubFrame title="Residence" icon={<MapPin size={13}/>}>
              {personal.city || personal.state || personal.country ? (
                <>
                  <Field label="Country">
                    <Input value={personal.country} onChange={v => setPersonal(p => ({...p, country:v}))} placeholder="Country" disabled={residenceApplied || d} />
                  </Field>
                  <Field label="State">
                    <Input value={personal.state} onChange={v => setPersonal(p => ({...p, state:v}))} placeholder="State" disabled={residenceApplied || d} />
                  </Field>
                  <Field label="City">
                    <Input value={personal.city} onChange={v => setPersonal(p => ({...p, city:v}))} placeholder="City" disabled={residenceApplied || d} />
                  </Field>
                  <Field label="Street Address">
                    <Input value={personal.residentialAddress} onChange={v => setPersonal(p => ({...p, residentialAddress:v}))} placeholder="e.g. house # and street name" disabled={residenceApplied || d} />
                  </Field>
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'4px' }}>
                    {residenceApplied
                      ? <AppliedTick label="Updated" />
                      : <UpdateBtn onClick={handleApplyResidence} disabled={savingResidence || d} saving={savingResidence} />
                    }
                  </div>
                </>
              ) : (
                <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No location detected in your CV.</p>
              )}
            </SubFrame>
          </>
        );

      // ── Professional Summary ─────────────────────────
      case 1:
        return (
          <>
            {!briefSummary && !fullSummary && (
              <p style={{ fontSize:'13px', color:C.muted, margin:'0 0 16px' }}>No summary detected in your CV. You can enter one manually below.</p>
            )}
            <Field label="Brief Summary">
              <Textarea value={briefSummary} onChange={setBriefSummary} rows={4} maxLength={500} disabled={briefApplied || d} />
              <CharCount current={(briefSummary || '').length} max={500} />
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'6px' }}>
                {briefApplied
                  ? <AppliedTick label="Updated" />
                  : <UpdateBtn onClick={handleApplyBrief} disabled={savingBrief || !briefSummary || d} saving={savingBrief} />
                }
              </div>
            </Field>
            <Field label="Full Summary">
              <Textarea value={fullSummary} onChange={setFullSummary} rows={10} maxLength={2000} disabled={fullApplied || d} />
              <CharCount current={(fullSummary || '').length} max={2000} />
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'6px' }}>
                {fullApplied
                  ? <AppliedTick label="Updated" />
                  : <UpdateBtn onClick={handleApplyFull} disabled={savingFull || !fullSummary || d} saving={savingFull} />
                }
              </div>
            </Field>
          </>
        );

      // ── Qualifications ───────────────────────────────
      case 2:
        return education.length === 0 ? (
          <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No education detected in your CV.</p>
        ) : education.map((entry, i) => {
          const applied = !!eduApplied[i];
          return (
            <div key={entry._key} style={{ border:`1px solid ${applied ? C.greenBorder : C.border}`, borderRadius:'4px', marginBottom:'12px', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 12px', background: applied ? C.green : C.blue }}>
                <span style={{ fontSize:'12px', fontWeight:700, color:'#fff', fontFamily:"'Exo 2', sans-serif" }}>
                  {entry.institution || 'Unknown institution'}
                </span>
                {!applied && !d && (
                  <button onClick={() => setEducation(prev => prev.filter((_, j) => j !== i))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.8)', padding:2, display:'flex' }}>
                    <X size={13}/>
                  </button>
                )}
              </div>
              <div style={{ padding:'14px' }}>
                <Field label="Qualification Level" required>
                  <select value={entry.degree || ''} onChange={ev => setEducation(prev => prev.map((e,j) => j===i ? {...e, degree: ev.target.value} : e))} disabled={applied || d}
                    style={{ width:'100%', padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white, boxSizing:'border-box' }}>
                    <option value="">— Select —</option>
                    {QUAL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Field of Study" required>
                  <Input value={entry.fieldOfStudy} onChange={v => setEducation(prev => prev.map((e,j) => j===i ? {...e, fieldOfStudy:v} : e))} placeholder="e.g. Accounting" disabled={applied || d} />
                </Field>
                <Field label="Institution">
                  <Input value={entry.institution} onChange={v => setEducation(prev => prev.map((e,j) => j===i ? {...e, institution:v} : e))} placeholder="University / School" disabled={applied || d} />
                </Field>
                <Field label="Enrolled">
                  <div style={{ display:'flex', gap:'6px' }}>
                    <select value={entry.startYear || ''} onChange={ev => setEducation(prev => prev.map((e,j) => j===i ? {...e, startYear: ev.target.value ? parseInt(ev.target.value) : null, startMonth: ev.target.value ? e.startMonth : ''} : e))} disabled={applied || d}
                      style={{ flex:1, padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white }}>
                      <option value="">Year (optional)</option>
                      {EDU_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={entry.startMonth || ''} onChange={ev => setEducation(prev => prev.map((e,j) => j===i ? {...e, startMonth: ev.target.value} : e))} disabled={applied || d || !entry.startYear}
                      style={{ flex:1, padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d||!entry.startYear)?C.offwhite:C.white }}>
                      <option value="">Month (optional)</option>
                      {EDU_MONTHS.map((m, mi) => {
                        if (entry.startYear === _cy && mi + 1 > _cm) return null;
                        return <option key={mi+1} value={String(mi+1)}>{m}</option>;
                      })}
                    </select>
                  </div>
                </Field>
                <Field label="Graduated">
                  <div style={{ display:'flex', gap:'6px' }}>
                    <select value={entry.endYear || ''} onChange={ev => setEducation(prev => prev.map((e,j) => j===i ? {...e, endYear: ev.target.value ? parseInt(ev.target.value) : null, endMonth: ev.target.value ? e.endMonth : '', isCompleted: !!ev.target.value} : e))} disabled={applied || d}
                      style={{ flex:1, padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white }}>
                      <option value="">Year (optional)</option>
                      {EDU_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={entry.endMonth || ''} onChange={ev => setEducation(prev => prev.map((e,j) => j===i ? {...e, endMonth: ev.target.value} : e))} disabled={applied || d || !entry.endYear}
                      style={{ flex:1, padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d||!entry.endYear)?C.offwhite:C.white }}>
                      <option value="">Month (optional)</option>
                      {EDU_MONTHS.map((m, mi) => {
                        if (entry.endYear === _cy && mi + 1 > _cm) return null;
                        return <option key={mi+1} value={String(mi+1)}>{m}</option>;
                      })}
                    </select>
                  </div>
                </Field>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'10px' }}>
                  {applied
                    ? <span title="Already added to profile" style={{ display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                    : <AddBtn onClick={() => handleApplyEdu(entry, i)} disabled={savingEdu[i] || d} saving={savingEdu[i]} />
                  }
                </div>
              </div>
            </div>
          );
        });

      // ── Experience ───────────────────────────────────
      case 3:
        return experience.length === 0 ? (
          <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No work experience detected in your CV.</p>
        ) : experience.map((entry, i) => {
          const applied = !!expApplied[i];
          const hint    = formatDuration(entry.durationMonths);
          return (
            <div key={entry._key} style={{ border:`1px solid ${applied ? C.greenBorder : C.border}`, borderRadius:'4px', marginBottom:'12px', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 12px', background: applied ? C.green : C.blue }}>
                <span style={{ fontSize:'12px', fontWeight:700, color:'#fff', fontFamily:"'Exo 2', sans-serif" }}>
                  {entry.jobTitle || 'Untitled position'}
                </span>
                {!applied && !d && (
                  <button onClick={() => setExperience(prev => prev.filter((_, j) => j !== i))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.8)', padding:2, display:'flex' }}>
                    <X size={13}/>
                  </button>
                )}
              </div>
              <div style={{ padding:'14px' }}>
                <Field label="Position Name">
                  <Input value={entry.jobTitle} onChange={v => setExperience(prev => prev.map((e,j) => j===i ? {...e, jobTitle:v} : e))} placeholder="e.g. Senior Accountant" disabled={applied || d} />
                </Field>
                <Field label="Employer Name">
                  <Input value={entry.company} onChange={v => setExperience(prev => prev.map((e,j) => j===i ? {...e, company:v} : e))} placeholder="e.g. Deloitte" disabled={applied || d} />
                </Field>
                <Field label="Location">
                  <Input value={entry.location || ''} onChange={v => setExperience(prev => prev.map((e,j) => j===i ? {...e, location:v} : e))} placeholder="e.g. Sydney, NSW" disabled={applied || d} />
                </Field>
                <Field label="Schedule">
                  <div style={{ display:'flex', gap:'20px', flexWrap:'wrap', paddingTop:'4px' }}>
                    {['Full-time', 'Part-time', 'Casual', 'Consultancy'].map(opt => (
                      <label key={opt} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', color:C.text, cursor:(applied||d)?'default':'pointer', fontWeight:400 }}>
                        <input
                          type="radio"
                          name={`schedule-import-${i}`}
                          value={opt}
                          checked={entry.schedule === opt}
                          onChange={() => { if (!applied && !d) setExperience(prev => prev.map((e,j) => j===i ? {...e, schedule:opt} : e)); }}
                          onClick={() => { if (!applied && !d && entry.schedule === opt) setExperience(prev => prev.map((e,j) => j===i ? {...e, schedule:''} : e)); }}
                          disabled={applied || d}
                          style={{ accentColor:C.blue, cursor:(applied||d)?'default':'pointer', width:'15px', height:'15px' }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </Field>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  <Field label="Date Started">
                    <div style={{ display:'flex', gap:'8px' }}>
                      <input
                        type="number"
                        value={entry.dateStarted ? entry.dateStarted.slice(0, 4) : ''}
                        onChange={ev => {
                          const y = ev.target.value;
                          const m = entry.dateStarted && entry.dateStarted.length >= 7 ? entry.dateStarted.slice(5, 7) : '';
                          setExperience(prev => prev.map((ex,j) => j===i ? {...ex, dateStarted: y ? (m ? `${y}-${m}` : y) : ''} : ex));
                        }}
                        placeholder="YYYY"
                        min="1950"
                        max="2099"
                        disabled={applied || d}
                        style={{ width:'80px', padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white, boxSizing:'border-box' }}
                      />
                      <select
                        value={entry.dateStarted && entry.dateStarted.length >= 7 ? entry.dateStarted.slice(5, 7) : ''}
                        onChange={ev => {
                          const m = ev.target.value;
                          const y = entry.dateStarted ? entry.dateStarted.slice(0, 4) : '';
                          setExperience(prev => prev.map((ex,j) => j===i ? {...ex, dateStarted: y ? (m ? `${y}-${m}` : y) : ''} : ex));
                        }}
                        disabled={applied || d}
                        style={{ flex:1, padding:'8px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white }}
                      >
                        <option value="">Month (optional)</option>
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((name, idx2) => (
                          <option key={idx2} value={String(idx2 + 1).padStart(2, '0')}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </Field>
                  <Field label="Duration (months)">
                    <Input value={entry.durationMonths || ''} onChange={v => setExperience(prev => prev.map((e,j) => j===i ? {...e, durationMonths:v} : e))} placeholder="Leave blank if current" disabled={applied || d} />
                    {hint && <div style={{ fontSize:'11px', color:C.muted, marginTop:'3px' }}>{hint}</div>}
                  </Field>
                </div>
                <Field label="Skills">
                  {(entry.skills || []).length > 0 ? (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {(entry.skills || []).map((skillName, si) => {
                        const inProfile = profileSkillNames.has(skillName.toLowerCase());
                        return (
                          <span key={si} style={{
                            display:'inline-flex', alignItems:'center', gap:'5px',
                            fontSize:'12px', fontWeight:600, padding:'3px 10px', borderRadius:'12px',
                            background: inProfile ? C.greenBg : '#e8eef5',
                            color:      inProfile ? '#166534' : C.navy,
                            border:     `1px solid ${inProfile ? C.greenBorder : C.border}`,
                          }}>
                            {inProfile
                              ? <CheckCircle size={11} color={C.green}/>
                              : <span style={{ fontSize:'9px', background:C.blue, color:'#fff', padding:'1px 5px', borderRadius:'3px', fontWeight:700, letterSpacing:'0.03em' }}>NEW</span>
                            }
                            {skillName}
                            {!applied && !d && (
                              <button onClick={() => setExperience(prev => prev.map((ex,j) => j===i ? {...ex, skills: ex.skills.filter((_,k) => k!==si)} : ex))}
                                style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', color:C.muted }}>
                                <X size={10}/>
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ fontSize:'12px', color:C.muted, margin:0 }}>No skills extracted for this role</p>
                  )}
                </Field>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'4px' }}>
                  {applied
                    ? <span title="Already added to profile" style={{ display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                    : <AddBtn onClick={() => handleApplyExp(entry, i)} disabled={savingExp[i] || d} saving={savingExp[i]} />
                  }
                </div>
              </div>
            </div>
          );
        });

      // ── Skills ───────────────────────────────────────
      case 4: {
        if (skills.length === 0) {
          return <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No skills detected in your CV.</p>;
        }
        const cvExisting = skills.filter(s => profileSkillNames.has((s.displayName || '').toLowerCase()));
        const cvNew      = skills.filter(s => !profileSkillNames.has((s.displayName || '').toLowerCase()));
        return (
          <>
            {cvExisting.length > 0 && (
              <SubFrame title="Existing Profile Skills">
                <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                  {cvExisting.map((skill, i) => (
                    <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'12px', fontWeight:600, padding:'4px 10px', borderRadius:'12px', background:C.greenBg, color:'#166534', border:`1px solid ${C.greenBorder}` }}>
                      <CheckCircle size={11} color={C.green}/>
                      {skill.displayName}
                    </span>
                  ))}
                </div>
              </SubFrame>
            )}

            {cvNew.length > 0 ? (
              <SubFrame title="New Skills">
                <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'12px' }}>
                  {cvNew.map((skill, i) => {
                    const key = (skill.displayName || '').toLowerCase();
                    const selected = selectedSkillNames.has(key);
                    return (
                      <span key={i} onClick={() => {
                        if (skillsApplied || d) return;
                        setSelectedSkillNames(prev => {
                          const next = new Set(prev);
                          selected ? next.delete(key) : next.add(key);
                          return next;
                        });
                      }} style={{ display:'inline-flex', alignItems:'center', fontSize:'12px', fontWeight:600, padding:'4px 10px', borderRadius:'12px', cursor:(skillsApplied||d)?'default':'pointer', userSelect:'none',
                        background: selected ? C.navy : '#e8eef5',
                        color: selected ? '#fff' : C.muted,
                        border: `1px solid ${selected ? C.navy : C.border}`,
                        opacity: selected ? 1 : 0.5,
                      }}>
                        {skill.displayName}
                      </span>
                    );
                  })}
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  {skillsApplied
                    ? <span title="Skills added to profile" style={{ display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                    : <AddBtn onClick={handleApplyNewSkills} disabled={savingSkills || d} saving={savingSkills} />
                  }
                </div>
              </SubFrame>
            ) : cvExisting.length > 0 ? (
              <p style={{ fontSize:'13px', color:C.muted, margin:'4px 0 0' }}>All extracted skills are already in your profile.</p>
            ) : null}
          </>
        );
      }

      // ── Languages ────────────────────────────────────
      case 5:
        return languages.length === 0 ? (
          <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No languages detected in your CV.</p>
        ) : (
          <div>
            {languages.map((lang, i) => {
              const applied = !!langApplied[i];
              return (
                <div key={lang._key} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                  <input value={lang.language} onChange={e => setLanguages(prev => prev.map((l,j) => j===i ? {...l, language:e.target.value} : l))} placeholder="e.g. Spanish" disabled={applied || d}
                    style={{ flex:2, padding:'7px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white }} />
                  <select value={lang.proficiency || 'intermediate'} onChange={e => setLanguages(prev => prev.map((l,j) => j===i ? {...l, proficiency:e.target.value} : l))} disabled={applied || d}
                    style={{ flex:1, padding:'7px 10px', fontSize:'13px', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.text, background:(applied||d)?C.offwhite:C.white }}>
                    {['native','fluent','advanced','professional','intermediate','conversational','basic'].map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                  {!applied && !d && (
                    <button onClick={() => setLanguages(prev => prev.filter((_,j) => j!==i))}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', display:'flex', flexShrink:0 }}>
                      <X size={14}/>
                    </button>
                  )}
                  <div style={{ flexShrink:0 }}>
                    {applied
                      ? <span title="Already added to profile" style={{ display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                      : <AddBtn onClick={() => handleApplyLang(lang, i)} disabled={savingLang[i] || d} saving={savingLang[i]} />
                    }
                  </div>
                </div>
              );
            })}
          </div>
        );

      // ── References ───────────────────────────────────
      case 6:
        return references.length === 0 ? (
          <p style={{ fontSize:'13px', color:C.muted, margin:0 }}>No references detected in your CV.</p>
        ) : references.map((ref, i) => {
          const applied = !!refApplied[i];
          return (
            <div key={ref._key} style={{ border:`1px solid ${applied ? C.greenBorder : C.border}`, borderRadius:'4px', marginBottom:'12px', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 12px', background: applied ? C.green : C.blue }}>
                <span style={{ fontSize:'12px', fontWeight:700, color:'#fff', fontFamily:"'Exo 2', sans-serif" }}>
                  {ref.name || 'Reference'}
                </span>
                {!applied && !d && (
                  <button onClick={() => setReferences(prev => prev.filter((_,j) => j!==i))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.8)', padding:2, display:'flex' }}>
                    <X size={13}/>
                  </button>
                )}
              </div>
              <div style={{ padding:'14px' }}>
                <Field label="Full Name">
                  <Input value={ref.name} onChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, name:v} : r))} placeholder="e.g. Jane Smith" disabled={applied || d} />
                </Field>
                <Field label="Position Title">
                  <Input value={ref.jobTitle || ''} onChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, jobTitle:v} : r))} placeholder="e.g. Finance Manager" disabled={applied || d} />
                </Field>
                <Field label="Company / Organization">
                  <Input value={ref.company || ''} onChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, company:v} : r))} placeholder="e.g. Deloitte" disabled={applied || d} />
                </Field>
                <Field label="Email Address">
                  <Input value={ref.email || ''} onChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, email:v} : r))} placeholder="e.g. jane@company.com" disabled={applied || d} />
                </Field>
                <Field label="Phone Number">
                  <PhoneCapture
                    code={ref.phoneCode || '+61'}
                    number={ref.phoneNumber || ''}
                    label={ref.phoneLabel || ''}
                    onCodeChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, phoneCode:v} : r))}
                    onNumberChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, phoneNumber:v} : r))}
                    onLabelChange={v => setReferences(prev => prev.map((r,j) => j===i ? {...r, phoneLabel:v} : r))}
                    disabled={applied || d}
                  />
                </Field>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'10px' }}>
                  {applied
                    ? <span title="Already added to profile" style={{ display:'flex', cursor:'default' }}><CheckCircle size={20} color={C.green} /></span>
                    : <AddBtn onClick={() => handleApplyRef(ref, i)} disabled={savingRef[i] || d} saving={savingRef[i]} />
                  }
                </div>
              </div>
            </div>
          );
        });

      default: return null;
    }
  };

  // ── Main render ───────────────────────────────────────

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Review Extracted CV Data</h1>
        <p>Extracted from <strong>{cv.fileName}</strong>. Review and edit each section, then click Finish to apply.</p>
      </div>

      <div className="page-content-wide" style={{ marginTop: 24 }}>

        {nothingExtracted && (
          <div style={{ background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:'6px', padding:'14px 18px', marginBottom:20, display:'flex', gap:'10px', alignItems:'flex-start' }}>
            <AlertCircle size={16} color={C.amber} style={{ flexShrink:0, marginTop:2 }} />
            <div>
              <p style={{ margin:'0 0 4px', fontSize:'13px', fontWeight:600, color:C.amber }}>Limited data extracted</p>
              <p style={{ margin:0, fontSize:'12px', color:C.muted }}>We couldn't extract much from this document. The format may be image-based or heavily styled. You can still fill in your profile manually.</p>
            </div>
          </div>
        )}

        {/* Tab bar */}
        <TabBar active={activeTab} onChange={setActiveTab} />

        {/* Tab content */}
        <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'6px', padding:'20px 24px', minHeight:'300px' }}>
          {renderTab()}
        </div>

        {/* Navigation */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'16px', paddingBottom:'40px' }}>
          <button onClick={() => navigate('/seeker/profile')} className="btn btn-outline" style={{ fontSize:'13px' }}>
            Back to My CVs
          </button>
          <div style={{ display:'flex', gap:'8px' }}>
            {activeTab > 0 && (
              <button onClick={() => setActiveTab(t => t - 1)} className="btn btn-outline" style={{ fontSize:'13px' }}>
                ← Previous
              </button>
            )}
            {activeTab < TAB_NAMES.length - 1 ? (
              <button onClick={() => setActiveTab(t => t + 1)} className="btn btn-primary" style={{ fontSize:'13px' }}>
                Next →
              </button>
            ) : (
              <button onClick={handleFinish} disabled={finishing} className="btn btn-primary"
                style={{ fontSize:'13px', display:'inline-flex', alignItems:'center', gap:'6px' }}>
                {finishing ? <Loader size={13} style={{ animation:'spin 1s linear infinite' }}/> : <CheckCircle size={13}/>}
                Finish Review
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
